import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateClientReferralFormTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const contactInfo1Id = uuidv4();
  const contactInfo1BlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const contactInfo2Id = uuidv4();
  const contactInfo2BlockId = uuidv4();
  const contactInfo3Id = uuidv4();
  const contactInfo3BlockId = uuidv4();
  const longTextId = uuidv4();
  const longTextBlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  return {
    ...themes.coralMintBold,
    "collectionId": collectionId,
    "name": "Client Referral Form Template",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO-black.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "DETATCHED_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "Know someone who could use our services?",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Share their details and you'll earn a 10% thank-you reward if they come onboard.",
        "thirdContent": "REFER NOW",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 68,
        "horizontalAlignment": false,
        "media": {
          "provider": "UNSPLASH",
          "id": welcomeMediaId,
          "type": "image",
          "name": "close-up photography of high-rise building",
          "link": "https://unsplash.com/photos/3lqk4BQkCDw/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw3fHxidXNpbmVzcyUyMHBpbmt8ZW58MHx8fHwxNzY0NDY1MDc1fDA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": contactInfo1Id,
        "blockId": contactInfo1BlockId,
        "content": "Let's start with your full name.",
        "manditory": false,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "So we know who to thank!",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": true
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "EMAIL",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": emailId,
        "blockId": emailBlockId,
        "content": `And your email, {{${contactInfo1BlockId}__first_name}}?`,
        "manditory": false,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll send your reward code here once your referral signs up.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": contactInfo2Id,
        "blockId": contactInfo2BlockId,
        "content": "Awesome. Who would you like to refer?",
        "manditory": false,
        "order": 3,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Name of the person you're recommending.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": true
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": true
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": contactInfo3Id,
        "blockId": contactInfo3BlockId,
        "content": `Thanks! What's {{${contactInfo2BlockId}__first_name}}'s phone number?`,
        "manditory": false,
        "order": 4,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Someone from our team will reach out for a quick intro conversation.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": false
          },
          "last_name": {
            "required": false,
            "visible": false
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": true
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "LONG_TEXT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": longTextId,
        "blockId": longTextBlockId,
        "content": "Anything else we should know?",
        "manditory": false,
        "order": 5,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Share any context that might help us connect better.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": "Thanks so much for the referral!",
        "manditory": false,
        "order": 6,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll take it from here.",
        "thirdContent": "again",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": {
          "provider": "PEXEL",
          "id": endScreenMediaId,
          "type": "video",
          "name": "5981416",
          "link": "https://videos.pexels.com/video-files/5981416/5981416-hd_1080_1920_24fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/5981416/pictures/preview-0.jpeg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": false,
      }
    ],
    "snippets": [],
    "metaTitle": "Client Referral Form Template",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateClientReferralFormTemplate;